import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AssetManager } from 'src/app/asset-manager/asset-manager';
import { initCSS2DRenderer, createSimpleGeometryCenterLabels2DObjects, disposeGroupLinesAndLabels, GeometryLabelData, Label2DInfo, updateLabel2DScales } from 'src/app/digital-twin/graphics/labels-utils';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { ELEMENTSTYPESIDS } from 'projects/phdsoft/common/src/lib/shared/constants/elementsTypesIds';
import * as THREE from '@phdsoft/three';


@Injectable({
  providedIn: 'root'
})
export class ViewModeStructureLabelsService {

  // State management
  private _isActive = new BehaviorSubject<boolean>(false);
  public isActive$ = this._isActive.asObservable();

  private _selectedElementType = new BehaviorSubject<string>('');
  public selectedElementType$ = this._selectedElementType.asObservable();

  // Labels 3D management
  private labelRenderer: CSS2DRenderer;
  private labelsGroup: THREE.Group;
  private currentLabelInfos: Label2DInfo[] = [];
  private currentAssetManager: AssetManager | null = null;

  // Available structural element types
  public readonly allStructuralTypesSorted: string[] = [
    'Plate', 'Stiffener', 'FacePlate', 'Bracket', 'BeamPart', 'Cable', 'Pipe', 'PipeConnector', 'Anode'
  ];

  constructor() { }

  /**
   * Ativa o sistema de labels estruturais
   */
  public activateStructuralLabels(assetManager: AssetManager): void {
    if (!assetManager?.asset || !assetManager?.graphics) {
      console.warn('AssetManager or graphics not available');
      return;
    }

    this.currentAssetManager = assetManager;
    this._isActive.next(true);
    this.initializeLabelsSystem();

    console.log('🏷️ Structural labels system activated');
  }

  /**
   * Desativa o sistema de labels estruturais
   */
  public deactivateStructuralLabels(assetManager?: AssetManager): void {
    this.clearCurrentLabels();
    this._isActive.next(false);
    this._selectedElementType.next('');
    this.currentAssetManager = null;

    // Remove labels group from scene
    if (this.labelsGroup && assetManager?.graphics?.scene) {
      assetManager.graphics.scene.remove(this.labelsGroup as any);
      this.labelsGroup = null;
    }

    console.log('🏷️ Structural labels system deactivated');
  }

  /**
   * Mostra labels para um tipo específico de elemento
   */
  public showStructuralLabels(assetManager: AssetManager, elementType: string): void {
    if (!this._isActive.value || !elementType) {
      console.warn('Service not active or element type not specified');
      return;
    }

    this.currentAssetManager = assetManager;
    this._selectedElementType.next(elementType);

    this.initializeLabelsSystem();
    this.clearCurrentLabels();

    const geometryLabels = this.extractGeometryLabelDataFromStructuralElements(elementType);

    if (geometryLabels.length > 0) {
      this.currentLabelInfos = createSimpleGeometryCenterLabels2DObjects(
        this.labelsGroup,
        geometryLabels,
        this.currentAssetManager.graphics.camera as any
      );

      console.log(`🏷️ Displaying ${geometryLabels.length} labels for ${elementType}`);
    } else {
      console.warn(`No ${elementType} elements found to display labels`);
    }

    this.requestSceneRender();
  }

  /**
   * Atualiza/refresh as labels atuais
   */
  public refreshStructuralLabels(assetManager?: AssetManager): void {
    const manager = assetManager || this.currentAssetManager;
    const elementType = this._selectedElementType.value;

    if (!this._isActive.value || !elementType || !manager) {
      console.warn('Cannot refresh labels - service not active or missing data');
      return;
    }

    console.log(`🔄 Refreshing structural labels for ${elementType}`);
    this.showStructuralLabels(manager, elementType);
  }

  /**
   * Obtém os tipos de elementos estruturais disponíveis
   */
  public getAvailableStructuralElementTypes(assetManager: AssetManager): string[] {
    if (!assetManager?.asset) {
      return [];
    }

    const structuralElementTypes: string[] = [];
    const protectionTypes: string[] = [];

    try {
      // Get structure visible elements types from visible regions
      for (const region of assetManager.asset.GetStructureVisibleRegions()) {
        for (const type of region.getStructureVisibleElementsTypes()) {
          if (!structuralElementTypes.includes(type)) {
            structuralElementTypes.push(type);
          }
        }
      }

      // Get protection elements types from visible regions
      for (const region of assetManager.asset.GetProtectionElementsVisibleRegions()) {
        for (const type of region.getProtectionElementsVisibleElementsTypes()) {
          if (!protectionTypes.includes(type)) {
            protectionTypes.push(type);
          }
        }
      }

      structuralElementTypes.push(...protectionTypes);

      // Sort types in the same order as allStructuralTypesSorted
      structuralElementTypes.sort((a, b) =>
        this.allStructuralTypesSorted.indexOf(a) - this.allStructuralTypesSorted.indexOf(b)
      );
    } catch (error) {
      console.error('Error getting available structural element types:', error);
    }

    return structuralElementTypes;
  }

  /**
   * Verifica se o service está ativo
   */
  public get isActive(): boolean {
    return this._isActive.value;
  }

  /**
   * Obtém o tipo de elemento selecionado
   */
  public get selectedElementType(): string {
    return this._selectedElementType.value;
  }

  /**
   * Limpa todas as labels atuais
   */
  public clearLabels(): void {
    this.clearCurrentLabels();
  }

  /**
   * Updates the scale of labels based on camera distance
   */
  public updateLabelScales(): void {
    if (this._isActive.value && this.currentLabelInfos.length > 0) {
      updateLabel2DScales(this.currentLabelInfos, this.currentAssetManager.graphics.camera as any);
    }
  }

  restoreStructureLabelsViewMode(visualizationMode: string, assetManager: AssetManager): void {
    // Parse the detailed mode string
    const parts = visualizationMode.split('|');
    if (parts.length !== 2) {
      // Fallback to basic structure labels mode
      this.activateStructuralLabels(assetManager);
      return;
    }

    const [baseMode, elementType] = parts;
    if (baseMode !== 'StructuralLabel') {
      // Fallback to basic structure labels mode
      this.activateStructuralLabels(assetManager);
      return;
    }

    // First, activate the basic structure labels view mode
    this.activateStructuralLabels(assetManager);

    // Restore selected element type
    if (elementType && elementType.trim()) {
      this.showStructuralLabels(assetManager, elementType.trim());
    }
  }

  /**
   * Dispose de todos os recursos
   */
  public dispose(): void {
    this.deactivateStructuralLabels();

    if (this.labelRenderer) {
      this.labelRenderer = null;
    }
  }

  // Private methods

  private initializeLabelsSystem(): void {
    if (!this.currentAssetManager) {
      return;
    }

    const container = document.getElementById('ThreeJS');
    if (!this.labelRenderer && container) {
      this.labelRenderer = initCSS2DRenderer(container);
    }

    if (!this.labelsGroup) {
      this.labelsGroup = new THREE.Group();
      this.labelsGroup.name = 'StructuralLabelsGroup';
      this.currentAssetManager.graphics.scene.add(this.labelsGroup as any);
    }
  }

  private clearCurrentLabels(): void {
    if (this.labelsGroup) {
      disposeGroupLinesAndLabels(this.labelsGroup);
      this.currentLabelInfos = [];
      this.requestSceneRender();
    }
  }

  // Graphics only renders on request. clearCurrentLabels() drops the old label <div>s from the DOM
  // at once, while new CSS2DObjects only enter the DOM on the next labelRenderer.render(); without a
  // render request the labels vanish until the ~1 s safety render (they "blink").
  private requestSceneRender(): void {
    this.currentAssetManager?.graphics?.requestRender();
  }

  private extractGeometryLabelDataFromStructuralElements(elementType: string): GeometryLabelData[] {
    const geometryLabels: GeometryLabelData[] = [];

    if (!this.currentAssetManager?.asset) {
      return geometryLabels;
    }

    try {
      const structuralLabels = this.extractLabelsFromStructuralCampaigns(elementType);
      geometryLabels.push(...structuralLabels);

      const repairLabels = this.extractLabelsFromRepairCampaigns(elementType);
      geometryLabels.push(...repairLabels);

    } catch (error) {
      console.error('Error extracting geometry label data from structural elements:', error);
    }

    return geometryLabels;
  }

  /**
   * Extracts labels from structural campaigns (original logic preserved)
   * @param elementType - Type of structural element to extract
   * @returns Array of geometry label data from structural elements
   */
  private extractLabelsFromStructuralCampaigns(elementType: string): GeometryLabelData[] {
    const geometryLabels: GeometryLabelData[] = [];

    try {
      const visibleRegions = this.currentAssetManager.asset.GetStructureVisibleRegions();

      for (const region of visibleRegions) {
        for (const section of region.sections) {
          if (!section.visible) continue;

          // Get surface elements (like Plates)
          if (section.surfaceElements) {
            for (const element of section.surfaceElements) {
              if (!element.realVisibility) continue;
              if (this.isElementOfType(element, elementType)) {
                const label = this.getElementLabel(element);
                const centerPosition = this.getElementGeometryCenter(element);
                // Only add label if it's not empty and position is valid
                if (centerPosition && label && label.trim() !== '') {
                  geometryLabels.push({ position: centerPosition, label });
                }
              }
            }
          }

          // Get linear elements (like Stiffeners, Brackets, etc.)
          if (section.linearElements) {
            for (const element of section.linearElements) {
              if (!element.realVisibility) continue;
              if (this.isElementOfType(element, elementType)) {
                const label = this.getElementLabel(element);
                const centerPosition = this.getElementGeometryCenter(element);
                // Only add label if it's not empty and position is valid
                if (centerPosition && label && label.trim() !== '') {
                  geometryLabels.push({ position: centerPosition, label });
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error extracting labels from structural campaigns:', error);
    }

    return geometryLabels;
  }

  /**
   * @param elementType - Type of structural element to extract
   * @returns Array of geometry label data from repair elements
   */
  private extractLabelsFromRepairCampaigns(elementType: string): GeometryLabelData[] {
    const geometryLabels: GeometryLabelData[] = [];

    if (!this.currentAssetManager?.asset?.RepairCampaigns) {
      return geometryLabels;
    }

    try {

      // Get visible regions to filter repairs the same way as structural elements
      const visibleRegions = this.currentAssetManager.asset.GetStructureVisibleRegions();
      const visibleRegionIds = visibleRegions.map(region => region.Guid);
      const visibleSectionIds = visibleRegions.flatMap(region =>
        region.sections.filter(section => section.visible).map(section => section.Guid)
      );

      for (const repairCampaign of this.currentAssetManager.asset.RepairCampaigns) {
        // Process surface elements in repair campaigns
        if (repairCampaign.SurfaceElements) {
          for (const repairElement of repairCampaign.SurfaceElements) {
            if (this.isRepairElementOfType(repairElement, elementType) &&
                this.isRepairElementInVisibleContext(repairElement, visibleRegionIds, visibleSectionIds) &&
                this.isRepairElementVisible(repairElement)) {
              const label = this.getRepairElementLabel(repairElement);
              const centerPosition = this.getElementGeometryCenter(repairElement);
              // Only add label if it's not empty and position is valid
              if (centerPosition && label && label.trim() !== '') {
                geometryLabels.push({
                  position: centerPosition,
                  label: label
                });
              }
            }
          }
        }

        // Process linear elements in repair campaigns
        if (repairCampaign.LinearElements) {
          for (const repairElement of repairCampaign.LinearElements) {
            if (this.isRepairElementOfType(repairElement, elementType) &&
                this.isRepairElementInVisibleContext(repairElement, visibleRegionIds, visibleSectionIds) &&
                this.isRepairElementVisible(repairElement)) {
              const label = this.getRepairElementLabel(repairElement);
              const centerPosition = this.getElementGeometryCenter(repairElement);
              // Only add label if it's not empty and position is valid
              if (centerPosition && label && label.trim() !== '') {
                geometryLabels.push({
                  position: centerPosition,
                  label: label
                });
              }
            }
          }
        }

        // Also process general Elements array if it exists (some repairs might be stored here)
        if (repairCampaign.Elements) {
          for (const repairElement of repairCampaign.Elements) {
            if (this.isRepairElementOfType(repairElement, elementType) &&
                this.isRepairElementInVisibleContext(repairElement, visibleRegionIds, visibleSectionIds) &&
                this.isRepairElementVisible(repairElement)) {
              const label = this.getRepairElementLabel(repairElement);
              const centerPosition = this.getElementGeometryCenter(repairElement);
              // Only add label if it's not empty and position is valid
              if (centerPosition && label && label.trim() !== '') {
                geometryLabels.push({
                  position: centerPosition,
                  label: label
                });
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error extracting labels from repair campaigns:', error);
    }

    return geometryLabels;
  }

  /**
   * Checks if a repair element is of the specified type
   * @param repairElement - The repair element to check
   * @param elementType - The element type to match
   * @returns True if the repair element matches the type
   */
  private isRepairElementOfType(repairElement: any, elementType: string): boolean {
    const typeMap = {
      'Plate': ELEMENTSTYPESIDS.PLATE,
      'Stiffener': ELEMENTSTYPESIDS.STIFFENER,
      'FacePlate': ELEMENTSTYPESIDS.FACEPLATE,
      'Bracket': ELEMENTSTYPESIDS.BRACKET,
      'BeamPart': ELEMENTSTYPESIDS.BEAMPART,
      'Cable': ELEMENTSTYPESIDS.CABLE,
      'Pipe': ELEMENTSTYPESIDS.PIPE,
      'PipeConnector': ELEMENTSTYPESIDS.PIPE,
      'Anode': ELEMENTSTYPESIDS.ANODE
    };

    // Check both TypeId property and properties.TypeId
    return repairElement.TypeId === typeMap[elementType] ||
           repairElement.properties?.TypeId === typeMap[elementType];
  }

  /**
   * Checks if a repair element is in the visible context (region/section)
   * @param repairElement - The repair element to check
   * @param visibleRegionIds - Array of visible region IDs
   * @param visibleSectionIds - Array of visible section IDs
   * @returns True if the repair element is in a visible region/section
   */
  private isRepairElementInVisibleContext(repairElement: any, visibleRegionIds: string[], visibleSectionIds: string[]): boolean {
    try {
      // Check if repair element is in a visible region
      let inVisibleRegion = false;

      if (repairElement.regionId) {
        inVisibleRegion = visibleRegionIds.includes(repairElement.regionId);
      } else if (repairElement.regionIds && Array.isArray(repairElement.regionIds)) {
        // Check if any of the element's regions are visible
        inVisibleRegion = repairElement.regionIds.some(regionId => visibleRegionIds.includes(regionId));
      }

      // If not in visible region, don't show
      if (!inVisibleRegion) {
        return false;
      }

      // Check if repair element is in a visible section (if applicable)
      if (repairElement.SectionId) {
        const inVisibleSection = visibleSectionIds.includes(repairElement.SectionId);
        if (!inVisibleSection) {
          return false;
        }
      }
      return true;
    } catch (error) {
      console.warn('🔧 Error checking repair element visible context:', error);
      return false; // Default to not visible if we can't determine
    }
  }

  /**
   * Checks if a repair element is visible
   * @param repairElement - The repair element to check
   * @returns True if the repair element is visible
   */
  private isRepairElementVisible(repairElement: any): boolean {
    try {
      // Check if the 3D object is visible
      if (repairElement.SurfaceDesign?.visible === false) {
        return false;
      }

      // Check if the element is active/executed
      if (repairElement.isExecuted === false) {
        return true; // Show outlined repairs
      }

      // Check reference date compatibility (if repair is within reference date)
      if (this.currentAssetManager?.referenceDate && repairElement.date) {
        const repairDate = new Date(repairElement.date);
        const referenceDate = new Date(this.currentAssetManager.referenceDate);
        if (repairDate > referenceDate) {
          return false;
        }
      }
      return true;
    } catch (error) {
      console.warn('🔧 Error checking repair element visibility:', error);
      return true; // Default to visible if we can't determine
    }
  }

  /**
   * Gets the label for a repair element
   * @param repairElement - The repair element to get label from
   * @returns The label string or empty string if not found
   */
  private getRepairElementLabel(repairElement: any): string {
    try {
      // First try the standard Label property from properties
      if (repairElement.properties?.General?.Label) {
        return repairElement.properties.General.Label;
      }

      // Check for repair-specific complete labels
      if (repairElement.properties?.General) {
        const general = repairElement.properties.General;

        // Check for complete labels similar to structural elements with filtering
        if (repairElement.TypeId === ELEMENTSTYPESIDS.STIFFENER || repairElement.properties?.TypeId === ELEMENTSTYPESIDS.STIFFENER) {
          if (general.CompleteStiffenerId) {
            if (repairElement.properties.Geometry?.FlangeThickness && repairElement.properties.Geometry.FlangeThickness > 0) {
              return ''; // Don't show label for flanges as per existing logic
            }
            if (general.CompleteStiffenerLabel) {
              return general.CompleteStiffenerLabel;
            }
          }
        }

        if (repairElement.TypeId === ELEMENTSTYPESIDS.BRACKET || repairElement.properties?.TypeId === ELEMENTSTYPESIDS.BRACKET) {
          if (general.CompleteBracketId) {
            if (repairElement.properties.Geometry?.FlangeThickness && repairElement.properties.Geometry.FlangeThickness > 0) {
              return ''; // Don't show label for flanges as per existing logic
            }
            if (general.CompleteBracketLabel) {
              return general.CompleteBracketLabel;
            }
          }
        }
      }

      // Try direct Label property
      if (repairElement.Label) {
        return repairElement.Label;
      }

      // Try Name property
      if (repairElement.Name) {
        return repairElement.Name;
      }

      // Fallback to a descriptive label with part of the ID
      if (repairElement.Guid) {
        return `Repair_${repairElement.Guid.substring(0, 8)}`;
      }

      return '';
    } catch (error) {
      console.warn('🔧 Error getting repair element label:', error);
      return '';
    }
  }

  private isElementOfType(element: any, elementType: string): boolean {
    const typeMap = {
      'Plate': ELEMENTSTYPESIDS.PLATE,
      'Stiffener': ELEMENTSTYPESIDS.STIFFENER,
      'FacePlate': ELEMENTSTYPESIDS.FACEPLATE,
      'Bracket': ELEMENTSTYPESIDS.BRACKET,
      'BeamPart': ELEMENTSTYPESIDS.BEAMPART,
      'Cable': ELEMENTSTYPESIDS.CABLE,
      'Pipe': ELEMENTSTYPESIDS.PIPE,
      'PipeConnector': ELEMENTSTYPESIDS.PIPE, // Using same as Pipe
      'Anode': ELEMENTSTYPESIDS.ANODE
    };

    return element.properties?.TypeId === typeMap[elementType];
  }

  private getElementLabel(element: any): string {
    const properties = element.properties;


    if (properties?.TypeId === ELEMENTSTYPESIDS.STIFFENER) {

      if (properties.General?.CompleteStiffenerId) {

        if (properties.Geometry?.FlangeThickness && properties.Geometry.FlangeThickness > 0) {
          return '';
        }

        const label = properties.General?.CompleteStiffenerLabel || properties.General?.Label || element.Name || 'Unlabeled';
        return label;
      }
    }


    if (properties?.TypeId === ELEMENTSTYPESIDS.BRACKET) {

      if (properties.General?.CompleteBracketId) {

        if (properties.Geometry?.FlangeThickness && properties.Geometry.FlangeThickness > 0) {
          return '';
        }

        const label = properties.General?.CompleteBracketLabel || properties.General?.Label || element.Name || 'Unlabeled';
        return label;
      }
    }

    return properties?.General?.Label || element.Name || 'Unlabeled';
  }

  private getElementGeometryCenter(element: any): THREE.Vector3 | null {
    try {
      // Strategy 1: Try to get bounding box from the element's 3D object
      const center = this.tryGetBoundingBoxCenter(element);
      if (center && this.isValidPosition(center)) {
        return center;
      }

      // Strategy 2: Try to calculate center from vertex positions
      const vertexCenter = this.tryGetVertexCenter(element);
      if (vertexCenter && this.isValidPosition(vertexCenter)) {
        return vertexCenter;
      }

      // Strategy 3: Try alternative geometry access paths
      const altCenter = this.tryAlternativeGeometryCenter(element);
      if (altCenter && this.isValidPosition(altCenter)) {
        return altCenter;
      }

      console.warn('Could not determine valid geometry center for element:', element.Name || 'Unnamed');
      return null;
    } catch (error) {
      console.error('Error getting element geometry center:', error);
      return null;
    }
  }

  private tryGetBoundingBoxCenter(element: any): THREE.Vector3 | null {
    try {
      // Try multiple possible geometry access paths
      const geometryObjects = [
        element.SurfaceDesign,
        element.geometry,
        element.mesh,
        element.object3d,
        element.threeObject
      ];

      for (const obj of geometryObjects) {
        if (obj) {
          try {
            const boundingBox = new THREE.Box3();
            boundingBox.setFromObject(obj);

            // Check if bounding box is valid (not empty/infinite)
            if (boundingBox.isEmpty() ||
                !isFinite(boundingBox.min.x) || !isFinite(boundingBox.min.y) || !isFinite(boundingBox.min.z) ||
                !isFinite(boundingBox.max.x) || !isFinite(boundingBox.max.y) || !isFinite(boundingBox.max.z)) {
              continue;
            }

            const center = new THREE.Vector3();
            boundingBox.getCenter(center);

            return center;
          } catch (e) {
            // Continue to next object
            continue;
          }
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  private tryGetVertexCenter(element: any): THREE.Vector3 | null {
    try {
      let positions: number[] = [];

      // Try to extract vertex positions from various sources
      const positionSources = [
        element.SurfaceDesign?.geometry?.attributes?.position?.array,
        element.geometry?.attributes?.position?.array,
        element.vertices,
        element.points,
        element.coordinates
      ];

      for (const source of positionSources) {
        if (source && Array.isArray(source) && source.length >= 3) {
          positions = source;
          break;
        }
      }

      if (positions.length < 3) {
        return null;
      }

      // Calculate average position from vertices
      let sumX = 0, sumY = 0, sumZ = 0;
      const vertexCount = Math.floor(positions.length / 3);

      for (let i = 0; i < vertexCount; i++) {
        sumX += positions[i * 3];
        sumY += positions[i * 3 + 1];
        sumZ += positions[i * 3 + 2];
      }

      return new THREE.Vector3(
        sumX / vertexCount,
        sumY / vertexCount,
        sumZ / vertexCount
      );
    } catch (error) {
      return null;
    }
  }

  private tryAlternativeGeometryCenter(element: any): THREE.Vector3 | null {
    try {
      // Try to use element's transform/position if available
      if (element.position) {
        if (element.position.x !== undefined && element.position.y !== undefined && element.position.z !== undefined) {
          return new THREE.Vector3(element.position.x, element.position.y, element.position.z);
        }
      }

      // Try to use center property if available
      if (element.center) {
        if (element.center.x !== undefined && element.center.y !== undefined && element.center.z !== undefined) {
          return new THREE.Vector3(element.center.x, element.center.y, element.center.z);
        }
      }

      // Try using centroid from element properties
      if (element.properties?.centroid) {
        const centroid = element.properties.centroid;
        if (centroid.x !== undefined && centroid.y !== undefined && centroid.z !== undefined) {
          return new THREE.Vector3(centroid.x, centroid.y, centroid.z);
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  private isValidPosition(position: THREE.Vector3): boolean {
    // Check if position has finite values and is not NaN
    if (isFinite(position.x) && isFinite(position.y) && isFinite(position.z) &&
        !isNaN(position.x) && !isNaN(position.y) && !isNaN(position.z)) {
      return true;
    }
    return false;
  }
}
